import { useSelection } from "@/contexts/SelectionContext";
import { Button, Modal } from "@ai-teach/ui-web";
import { useState } from "react";
import { LuCopy, LuCopyCheck } from "react-icons/lu";

interface WarningModalProps {
  isOpen: boolean;
}

function formatErrorForCopy(error: unknown, message: string): string {
  if (!error) return message;

  return JSON.stringify(error, null, 2);
}

export function WarningModal({ isOpen }: WarningModalProps) {
  const { toggleWarningModal, isWarningModalOpen, warningCallbacks, warningError } = useSelection();
  const [copied, setCopied] = useState(false);

  const handleConfirm = () => {
    warningCallbacks?.onConfirm();
    toggleWarningModal(false);
  };

  const handleCancel = () => {
    warningCallbacks?.onCancel();
    toggleWarningModal(false);
  };

  // X / backdrop / Escape: just dismiss and stay — don't run the (possibly navigating) onCancel.
  const handleDismiss = () => {
    toggleWarningModal(false);
  };

  const handleCopyError = async () => {
    if (!isWarningModalOpen) return;

    try {
      const textToCopy = formatErrorForCopy(warningError, isWarningModalOpen);
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
    } catch (error) {
      console.error("Failed to copy error message:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDismiss}
      title={warningCallbacks?.title ?? "Warning"}
      size="sm"
    >
      <div className="flex flex-col">
        <div className="relative mb-6">
          <p className="text-secondary-200 text-center">{isWarningModalOpen}</p>
          {!!warningError && (
            <button
              type="button"
              onClick={handleCopyError}
              className="absolute -right-2 -top-2 p-1.5 text-gray-400 hover:text-white transition-colors rounded-md hover:bg-gray-800/50"
              title={copied ? "Copied!" : "Copy error message"}
            >
              {copied ? (
                <LuCopyCheck className="w-4 h-4 text-green-500" />
              ) : (
                <LuCopy className="w-4 h-4" />
              )}
            </button>
          )}
        </div>

        <div className="flex justify-center gap-3">
          {warningCallbacks && (
            <Button onClick={handleCancel} variant="danger">
              {warningCallbacks.cancelLabel ?? "Cancel"}
            </Button>
          )}

          <Button onClick={handleConfirm} variant="primary">
            {warningCallbacks?.confirmLabel ?? "Ok"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
